import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import actions from 'roles-actions-permissions/actions';
import usePermissions from 'hooks/permissions-hooks';
import Button from '@material-ui/core/Button';
import axiosClient from 'http/axios-client';
import * as _ from 'lodash';
import { LoadingButtonNoGrid } from 'components/basic/LoadingButton';
import * as moment from 'moment';
import MonthSelector from 'components/basic/MonthSelector';
import { SearchAndFilterButtons } from 'components/basic/SearchAndFilterButtons';
import { approvalApply } from 'utils/reportsUtils';
import { AiOutlineLineChart } from 'react-icons/ai';
import { getFullRegistrationStatus, parseDate } from '../consents/ConsentsTable';
import { roles } from '../../../cassandra-shared-js/roles';
import { filterTypes } from '../../basic/AdvancedFilters';
import AppTable from '../../basic/AppTable';
import AppLoader from '../../basic/AppLoader';
import { useItemsReports } from '../../../hooks/atoms/items-hook-reports';
import CollectionKeys from '../../../hooks/molecules/collection-keys';
import getTheme from '../../../theme/theme';
import CollectionItemHeader from '../../layout/CollectionItemHeader';

const downloadData = async (userHasRole, setDownloadAllLoading, filterQuery) => {
  setDownloadAllLoading(true);
  const theKits = await axiosClient.get(CollectionKeys.REGISTRATION_KITS_FOR_REPORTS, {
    params: {
      ...filterQuery,
    },
  });

  if (null == theKits) {
    setDownloadAllLoading(false);
    return;
  }

  setDownloadAllLoading(false);

  const getRealStatus = (status) => {
    if (false === status) {
      return 'false';
    }
    if (true === status) {
      return 'true';
    }
    return 'pending';
  };

  const data = theKits.map((kit) => {
    const obj = {
      'Serial Number': kit.serial_number_id?.serial ?? '',
      'Report delivery date': parseDate(kit.report_delivery_date) ?? '',
      'Thematic Package': kit.thematic_package?.thematic_category_id?.name ?? '',
      'Report language': kit.locale ?? '',
      // TODO: You should show this..
      // 'Questionnaire Availability': `${ hasQuestionnaireObj[kit.serial_number_id.serial] }` ?? 'false',
      'PDF Available': `${ true === kit?.pdf_available }`,
      'Bioinformatic Status': getRealStatus(kit?.analysis_approval),
      'Nutrition Genetics': getRealStatus(kit?.nutrition_geneticist_approval),
      'Medical Status': getRealStatus(kit?.cardiologist_approval),
      'Registration Status': getFullRegistrationStatus(kit),
    };

    if (!userHasRole(roles.ADMINISTRATOR_ROLE) || userHasRole(roles.ANALYTICS_DEPARTMENT_USER_ROLE)) {
      delete obj.locale;
    }

    if (userHasRole(roles.QUALITY_DEPARTMENT_USER_ROLE) || userHasRole(roles.QUALITY_DEPARTMENT_USER_ROLE_2)) {
      delete obj['PDF Available'];
      delete obj['Medical Status'];
      delete obj['Registration Status'];
    } else if (userHasRole(roles.THIRD_PARTY_CARDIOLOGIST)) {
      delete obj['Thematic Package'];
      delete obj['PDF Available'];
      delete obj['Bioinformatic Status'];
      delete obj['Registration Status'];
    }
    return obj;
  });

  const worksheet = XLSX.utils.json_to_sheet(data);
  worksheet['!cols'] = fitToColumn(data);

  function fitToColumn(arrayOfArray) {
    if (!arrayOfArray[0]) { // If file is empty
      return null;
    }
    return Object.keys(arrayOfArray[0]).map((key) => {
      return { wch: key.length };
    });
  }

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
  XLSX.writeFile(workbook, 'Nutrition Reports.xlsx');
};

async function multipleApprove(setApprovalLoading, filterQuery, client, selected, url, field, setShowLoader, refetch, setSelected, value = true) {
  const toBePosted = [];

  setApprovalLoading(true);
  const kits = await client.get(CollectionKeys.REGISTRATION_KITS_FOR_REPORTS, {
    params: {
      ...filterQuery,
    },
  });

  if (null == kits) {
    setApprovalLoading(false);
    return;
  }
  setApprovalLoading(false);

  selected.forEach((id) => {
    const kit = kits.find((theKit) => {
      return theKit.id === id;
    });

    if (kit === undefined || 'done' === kit.registrationStatus || true !== kit.pdf_available) { return; }

    if ('analysis_approval' === field && value === kit.analysis_approval) {
      return;
    }

    if ('nutrition_geneticist_approval' === field && (true !== kit.analysis_approval || value === kit.nutrition_geneticist_approval)) {
      return;
    }

    if ('cardiologist_approval' === field && (true !== kit.nutrition_geneticist_approval || value === kit.cardiologist_approval)) {
      return;
    }

    toBePosted.push(kit.serial_number_id.serial);
  });

  if (0 === toBePosted.length) {
    return;
  }

  setShowLoader(true);

  if ('cardiologist_approval' === field) {
    const promises = toBePosted.map((serial) => {
      return client.put(`${ url }/${ serial }`, {
        [field]: value,
      });
    });

    Promise.all([ ...promises ])
      .finally(() => {
        Promise.all([ refetch() ]).finally(() => {
          setSelected([]);
          setShowLoader(false);
        });
      });
  } else {
    const requestBody = toBePosted.map(((serial) => {
      return {
        serial,
        [field]: value,
      };
    }));

    client.put(url, requestBody)
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        refetch()
          .then(() => {
            setSelected([]);
            setShowLoader(false);
          });
      });
  }
}

const NutritionReportsTable = () => {
  const [ selected, setSelected ] = useState([]);
  const [ filterQuery, setFilterQuery ] = useState({ });
  const [ loading, setLoading ] = useState(false);
  const [ selectedMonths, setSelectedMonths ] = useState(1);

  const handleChange = (event) => { setSelectedMonths(event.target.value); };

  const startDate = moment().subtract(selectedMonths, 'months').startOf('day').toISOString();

  const [ downloadAllLoading, setDownloadAllLoading ] = useState(false);
  const [ downloadLoading, setDownloadLoading ] = useState(false);
  const [ approvalLoading, setApprovalLoading ] = useState(false);

  let kitsFilterQuery = {
    ...filterQuery,
    'thematic_package.thematic_category_id.name_eq': 'Nutrition',
    _where: [
      { _or:
        filterQuery?._where?._or },

      { _or: [
        [{ 'serial_number_id.report_payment_status_eq': 'paid' }],
        [{ 'serial_number_id.report_payment_status_eq': 'free' }],
      ] },
    ],
  };

  let allKitsFilterQuery = {
    ...filterQuery,
    'thematic_package.thematic_category_id.name_eq': 'Nutrition',
    _limit: -1,
    _where: [
      { _or:
        filterQuery?._where?._or },

      { _or: [
        [{ 'serial_number_id.report_payment_status_eq': 'paid' }],
        [{ 'serial_number_id.report_payment_status_eq': 'free' }],
      ] },
    ],
  };

  const { hasPermissions, userHasRole } = usePermissions();

  if (userHasRole(roles.THIRD_PARTY_CARDIOLOGIST)) {
    kitsFilterQuery = {
      ...kitsFilterQuery,
      nutrition_geneticist_approval: true,
      pdf_available_eq: true,
      'serial_number_id.ancestor_null': true,
    };

    allKitsFilterQuery = {
      ...allKitsFilterQuery,
      nutrition_geneticist_approval: true,
      pdf_available_eq: true,
      'serial_number_id.ancestor_null': true,
    };
  } else if (userHasRole(roles.NUTRITION_GENETICS_USER)) {
    kitsFilterQuery = {
      ...kitsFilterQuery,
      analysis_approval: true,
      pdf_available_eq: true,
      'serial_number_id.ancestor_null': true,
    };

    allKitsFilterQuery = {
      ...allKitsFilterQuery,
      analysis_approval: true,
      pdf_available_eq: true,
      'serial_number_id.ancestor_null': true,
    };
  } else if (hasPermissions(actions.ANALYTICS_REPORT_FINALIZE_EDIT)) {
    kitsFilterQuery = {
      ...kitsFilterQuery,
      pdf_available_eq: true,
      'serial_number_id.ancestor_null': true,
    };

    allKitsFilterQuery = {
      ...allKitsFilterQuery,
      pdf_available_eq: true,
      'serial_number_id.ancestor_null': true,
    };
  }

  const {
    items: kits,
    itemsLoading: kitsLoading,
    pagination,
    refetch,
  } = useItemsReports(CollectionKeys.REGISTRATION_KITS_FOR_REPORTS, {
    filterQuery: kitsFilterQuery,
  });

  const {
    items: allKits,
    itemsLoading: allKitsLoading,
    refetch: allKitsRefetch,
    countFiltered,
  } = useItemsReports(CollectionKeys.REGISTRATION_KITS_FOR_REPORTS, {
    filterQuery: allKitsFilterQuery,
  });

  if (kitsLoading || allKitsLoading) {
    return <AppLoader />;
  }

  let tableColumns = [
    { id: 1, label: 'Serial Number', field: 'serial_number_id.serial' },
    { id: 220,
      label: 'Report delivery date',
      field: 'report_delivery_date',
      customRenderer({ row }) {
        return (
          <>
            {parseDate(row.report_delivery_date)}
          </>
        );
      } },
    { id: 2, label: 'Thematic Package', field: 'thematic_package.thematic_category_id.name', noSort: true },
    { id: 9834, label: 'Download Report', field: 'pdf_available', downloadReport: true },
    ...(userHasRole(roles.ADMINISTRATOR_ROLE) || userHasRole(roles.ANALYTICS_DEPARTMENT_USER_ROLE)) ? [{
      id: 931,
      label: 'Report language',
      field: 'locale',
      kitLang: true,
    }] : [],
    { id: 4, label: 'PDF Available', field: 'pdf_available', doneOrButton: true },
  ];

  if (hasPermissions(actions.GENERAL_REPORT_FINALIZE_EDIT)) {
    tableColumns = [
      { id: 1, label: 'Serial Number', field: 'serial_number_id.serial' },
      { id: 3, label: 'Download Report', field: 'pdf_available', downloadReport: true },
    ];
  }

  if (hasPermissions(actions.ANALYTICS_REPORT_FINALIZE_VIEW)) {
    tableColumns.push(
      { id: 9,
        label: 'Bioinformatic Status',
        field: 'analysis_approval',
        noSort: false,
        customRenderer({
          row,
        }) {
          const disabled = false === row.analysis_approval && 'done' !== row.registrationStatus;

          let value = '';
          if (null === row.analysis_approval) {
            value = 'Pending';
          } else if (!disabled) {
            value = 'Approved';
          } else {
            value = 'Disapproved';
          }

          return (
            <div style={ { display: 'flex', justifyContent: 'flex-start' } }>
              {value}
            </div>
          );
        } },
      ...hasPermissions(actions.ANALYTICS_REPORT_FINALIZE_EDIT)
        ? [{ id: 79,
          label: 'Bioinformatic Approval',
          field: 'analysis_approval',
          noSort: true,
          customRenderer({
            setShowLoader,
            refetch: theRefetch,
            row,
          }) {
            const { serial } = row.serial_number_id;

            if (true !== row.pdf_available || 'done' === row.registrationStatus) {
              return <div />;
            }

            return (
              <div style={ { display: 'flex', justifyContent: 'center', gap: '5px' } }>
                {(null === row.analysis_approval || false === row.analysis_approval) && 'done' !== row.registrationStatus && (
                  <Button
                    variant='outlined'
                    color='secondary'
                    disabled={ !hasPermissions(actions.ANALYTICS_REPORT_FINALIZE_EDIT) }
                    onClick={ () => { approvalApply(`registration-kits/analysis-update/${ serial }`, 'analysis_approval', true, setShowLoader, theRefetch, allKitsRefetch, setSelected); } }
                  >
                    APPROVE
                  </Button>
                )}
                {null === row.analysis_approval && 'done' !== row.registrationStatus && (
                  <Button
                    disabled={ !hasPermissions(actions.ANALYTICS_REPORT_FINALIZE_EDIT) }
                    style={ { color: !hasPermissions(actions.ANALYTICS_REPORT_FINALIZE_EDIT) ? '' : 'red',
                      borderColor: !hasPermissions(actions.ANALYTICS_REPORT_FINALIZE_EDIT) ? 'rgba(0, 0, 0, 0.26)' : 'red' } }
                    variant='outlined'
                    onClick={ () => { approvalApply(`registration-kits/analysis-update/${ serial }`, 'analysis_approval', false, setShowLoader, theRefetch, allKitsRefetch, setSelected); } }
                  >
                    DISAPPROVE
                  </Button>

                )}
              </div>
            );
          } }] : [],
    );
  }

  if (userHasRole(roles.NUTRITION_GENETICS_USER) || userHasRole(roles.ADMINISTRATOR_ROLE) || userHasRole(roles.ANALYTICS_DEPARTMENT_USER_ROLE)) {
    tableColumns.push(
      { id: 122339,
        label: 'Nutrition Genetics Approval Status',
        field: 'nutrition_geneticist_approval',
        noSort: false,
        customRenderer({
          row,
        }) {
          const disabled = false === row.nutrition_geneticist_approval && 'done' !== row.registrationStatus;

          let value = '';
          if (null === row.nutrition_geneticist_approval) {
            value = 'Pending';
          } else if (!disabled) {
            value = 'Approved';
          } else {
            value = 'Disapproved';
          }

          return (
            <div style={ { display: 'flex', justifyContent: 'flex-start' } }>
              {value}
            </div>
          );
        } },
      ...userHasRole(roles.NUTRITION_GENETICS_USER)
        ? [{ id: 132579,
          label: 'Nutrition Genetics Approval',
          field: 'nutrition_geneticist_approval',
          noSort: true,
          customRenderer({
            setShowLoader,
            refetch: theRefetch,
            row,
          }) {
            const { serial } = row.serial_number_id;

            if (true !== row.pdf_available || 'done' === row.registrationStatus) {
              return <div />;
            }

            return (
              <div style={ { display: 'flex', justifyContent: 'center', gap: '5px' } }>
                { (null === row.nutrition_geneticist_approval || false === row.nutrition_geneticist_approval) && 'done' !== row.registrationStatus && (
                  <Button
                    variant='outlined'
                    color='secondary'
                    disabled={ !hasPermissions(actions.GENERAL_REPORT_FINALIZE_EDIT) }
                    onClick={ () => { approvalApply(`registration-kits/medical-geneticist-update/${ serial }`, 'nutrition_geneticist_approval', true, setShowLoader, theRefetch, allKitsRefetch, setSelected); } }
                  >
                    APPROVE
                  </Button>
                )}
                {null === row.nutrition_geneticist_approval && 'done' !== row.registrationStatus && (
                  <Button
                    disabled={ !hasPermissions(actions.GENERAL_REPORT_FINALIZE_EDIT) }
                    style={ { color: !hasPermissions(actions.GENERAL_REPORT_FINALIZE_EDIT) ? '' : 'red',
                      borderColor: !hasPermissions(actions.GENERAL_REPORT_FINALIZE_EDIT) ? 'rgba(0, 0, 0, 0.26)' : 'red' } }
                    variant='outlined'
                    onClick={ () => { approvalApply(`registration-kits/medical-geneticist-update/${ serial }`, 'nutrition_geneticist_approval', false, setShowLoader, theRefetch, allKitsRefetch, setSelected); } }
                  >
                    DISAPPROVE
                  </Button>
                )}
              </div>
            );
          } }] : [],
    );
  }

  if (userHasRole(roles.THIRD_PARTY_CARDIOLOGIST) || userHasRole(roles.ADMINISTRATOR_ROLE) || userHasRole(roles.ANALYTICS_DEPARTMENT_USER_ROLE)) {
    tableColumns.push(
      { id: 1239,
        label: 'Chief Scientific Officer Approval Status',
        field: 'cardiologist_approval',
        noSort: false,
        customRenderer({
          row,
        }) {
          const disabled = false === row.cardiologist_approval && 'done' !== row.registrationStatus;

          let value = '';
          if (null === row.cardiologist_approval) {
            value = 'Pending';
          } else if (!disabled) {
            value = 'Approved';
          } else {
            value = 'Disapproved';
          }

          return (
            <div style={ { display: 'flex', justifyContent: 'flex-start' } }>
              {value}
            </div>
          );
        } },
      ...userHasRole(roles.THIRD_PARTY_CARDIOLOGIST)
        ? [{ id: 12579,
          label: 'Chief Scientific Officer Approval',
          field: 'cardiologist_approval',
          noSort: true,
          customRenderer({
            setShowLoader,
            refetch: theRefetch,
            row,
          }) {
            const { serial } = row.serial_number_id;

            if (true !== row.pdf_available || 'done' === row.registrationStatus) {
              return <div />;
            }

            return (
              <div style={ { display: 'flex', justifyContent: 'center', gap: '5px' } }>
                { (null === row.cardiologist_approval || false === row.cardiologist_approval) && 'done' !== row.registrationStatus && (
                  <Button
                    variant='outlined'
                    color='secondary'
                    disabled={ !hasPermissions(actions.GENERAL_REPORT_FINALIZE_EDIT) }
                    onClick={ () => { approvalApply(`registration-kits/cardiologist-update/${ serial }`, 'cardiologist_approval', true, setShowLoader, theRefetch, allKitsRefetch, setSelected); } }
                  >
                    APPROVE
                  </Button>
                )}
                {null === row.cardiologist_approval && 'done' !== row.registrationStatus && (
                  <Button
                    disabled={ !hasPermissions(actions.GENERAL_REPORT_FINALIZE_EDIT) }
                    style={ { color: !hasPermissions(actions.GENERAL_REPORT_FINALIZE_EDIT) ? '' : 'red',
                      borderColor: !hasPermissions(actions.GENERAL_REPORT_FINALIZE_EDIT) ? 'rgba(0, 0, 0, 0.26)' : 'red' } }
                    variant='outlined'
                    onClick={ () => { approvalApply(`registration-kits/cardiologist-update/${ serial }`, 'cardiologist_approval', false, setShowLoader, theRefetch, allKitsRefetch, setSelected); } }
                  >
                    DISAPPROVE
                  </Button>
                )}
              </div>
            );
          } }] : [],
    );
  }

  if (!hasPermissions(actions.GENERAL_REPORT_FINALIZE_EDIT)
  && !userHasRole(roles.ANALYTICS_DEPARTMENT_USER_ROLE)
  && !userHasRole(roles.QUALITY_DEPARTMENT_USER_ROLE)
&& !userHasRole(roles.QUALITY_DEPARTMENT_USER_ROLE_2)
  ) {
    tableColumns.push(
      { id: 15,
        label: 'Registration Status',
        field: 'registrationStatus',
        noSort: true,
        customRenderer({ row }) {
          return (
            <>
              {
                getFullRegistrationStatus(row)
              }
            </>
          );
        } },
    );
  }

  return (
    <div>
      <div style={ { display: 'flex', flexDirection: 'row', alignItems: 'center', marginBottom: getTheme().spacing(2) } }>
        <AiOutlineLineChart
          style={ { color: getTheme().palette.secondary.dark, marginRight: getTheme().spacing(2) } }
          size={ 36 }
        />
        <CollectionItemHeader
          title='Nutrition Reports'
          subtitle={ `Entries: ${ countFiltered ?? 0 }` }
          actions={ (
            <>
            </>
          ) }
        />
      </div>
      <div style={ { display: 'flex', gap: '5px', marginBottom: '15px' } }>
        <span>
          <Button
            variant='contained'
            color='secondary'
            onClick={ () => {
              setSelected(allKits?.map((ar) => { return ar.id; }));
            } }
          >
            Select All
          </Button>
        </span>
        <span>
          <Button
            variant='contained'
            color='secondary'
            onClick={ () => {
              setSelected([]);
            } }
          >
            Clear Selection
          </Button>
        </span>
        {
          (userHasRole(roles.ANALYTICS_DEPARTMENT_USER_ROLE) || userHasRole(roles.NUTRITION_GENETICS_USER) || userHasRole(roles.THIRD_PARTY_CARDIOLOGIST)) && (
            <LoadingButtonNoGrid
              variant='contained'
              color='secondary'
              loading={ approvalLoading }
              onClick={ () => {
                if (userHasRole(roles.ANALYTICS_DEPARTMENT_USER_ROLE)) {
                  multipleApprove(setApprovalLoading, allKitsFilterQuery, axiosClient, selected, 'registration-kits/analysis-batch-update', 'analysis_approval', setLoading, refetch, setSelected);
                } else if (userHasRole(roles.NUTRITION_GENETICS_USER)) {
                  multipleApprove(setApprovalLoading, allKitsFilterQuery, axiosClient, selected, 'registration-kits/geneticist-batch-update', 'nutrition_geneticist_approval', setLoading, refetch, setSelected);
                } else {
                  multipleApprove(setApprovalLoading, allKitsFilterQuery, axiosClient, selected, 'registration-kits/cardiologist-update', 'cardiologist_approval', setLoading, refetch, setSelected);
                }
              } }
              label='Approve selected'
            />
          )
        }

        {
          (userHasRole(roles.ANALYTICS_DEPARTMENT_USER_ROLE) || userHasRole(roles.NUTRITION_GENETICS_USER) || userHasRole(roles.THIRD_PARTY_CARDIOLOGIST)) && (
            <LoadingButtonNoGrid
              variant='contained'
              color='secondary'
              loading={ approvalLoading }
              onClick={ () => {
                if (userHasRole(roles.ANALYTICS_DEPARTMENT_USER_ROLE)) {
                  multipleApprove(setApprovalLoading, allKitsFilterQuery, axiosClient, selected, 'registration-kits/analysis-batch-update', 'analysis_approval', setLoading, refetch, setSelected, false);
                } else if (userHasRole(roles.NUTRITION_GENETICS_USER)) {
                  multipleApprove(setApprovalLoading, allKitsFilterQuery, axiosClient, selected, 'registration-kits/geneticist-batch-update', 'nutrition_geneticist_approval', setLoading, refetch, setSelected, false);
                } else {
                  multipleApprove(setApprovalLoading, allKitsFilterQuery, axiosClient, selected, 'registration-kits/cardiologist-update', 'cardiologist_approval', setLoading, refetch, setSelected, false);
                }
              } }
              label='Disapprove selected'
            />
          )
        }

        <LoadingButtonNoGrid
          variant='contained'
          onClick={ () => { downloadData(userHasRole, setDownloadLoading, kitsFilterQuery); } }
          label='Download'
          loading={ downloadLoading }
        />

        <LoadingButtonNoGrid
          loading={ downloadAllLoading }
          variant='contained'
          label='Download Last'
          onClick={ () => {
            if (selectedMonths === 'All') {
              downloadData(userHasRole, setDownloadAllLoading, allKitsFilterQuery);
            } else {
              downloadData(userHasRole, setDownloadAllLoading, { ...allKitsFilterQuery, created_at_gte: startDate });
            }
          } }
        />
        <MonthSelector selectedMonths={ selectedMonths } handleChange={ handleChange } />
        {
          (userHasRole(roles.THIRD_PARTY_CARDIOLOGIST) || userHasRole(roles.QUALITY_DEPARTMENT_USER_ROLE)
          || userHasRole(roles.ANALYTICS_DEPARTMENT_USER_ROLE)
          || userHasRole(roles.NUTRITION_GENETICS_USER)
          ) && (
            <SearchAndFilterButtons
              setFilterQuery={ setFilterQuery }
              filterQuery={ filterQuery }
              reportType='NUTRITION'
            />
          )
        }

      </div>
      <AppTable
        columns={ tableColumns }
        data={ kits }
        loading={ kitsLoading || allKitsLoading || loading }
        paginationProps={ pagination }
        refetch={ refetch }
        advancedFiltersProps={
          userHasRole(roles.QUALITY_DEPARTMENT_USER_ROLE) || userHasRole(roles.QUALITY_DEPARTMENT_USER_ROLE_2)
          || userHasRole(roles.THIRD_PARTY_CARDIOLOGIST) || userHasRole(roles.NUTRITION_GENETICS_USER) || userHasRole(roles.ANALYTICS_DEPARTMENT_USER_ROLE)
            ? null
            : {
              properties: [
                {
                  value: 'serial_number_id.serial',
                  label: 'Serial Number',
                  type: filterTypes.FREE_TEXT,
                },
                {
                  value: 'analysis_approval',
                  label: 'Analysis approval',
                  type: filterTypes.CATEGORY,
                  options: [
                    { value: false, label: 'FALSE' },
                    { value: true, label: 'TRUE' },
                  ],
                },
                {
                  label: 'Registration Status ', value: 'registrationStatus', isRegistration: true, type: filterTypes.FREE_TEXT,
                },
                {
                  value: 'child_id',
                  label: 'Children',
                  type: filterTypes.CATEGORY,
                  options: [
                    { value: false, label: 'True' },
                    { value: true, label: 'False' },
                  ],
                },
              ],
              initialAndFilterOptions: [],
              onQueryChange: setFilterQuery,
            }
        }
        selectionProps={ { setSelected, selected } }
      />
    </div>
  );
};

export default NutritionReportsTable;
